import React, { useRef, useState } from "react";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_chassis_for_wo.css";
import useSWR from 'swr';
import axios from 'axios';
import dayjs from 'dayjs';
import { Resizable } from 'react-resizable';
import type { ResizeCallbackData } from 'react-resizable';

export interface ChassisDataType {
    kodePosStnk: any;
    key: React.Key;
    chassisNo: string;
    engineNo: string;
    noPol: string;
    stnkName: string;
    stnkAddress: string;
    provinsiStnk: string;
    provinsiStnkCode: string;
    kabupatenStnk: string;
    kabupatenStnkCode: string;
    kecamatanStnk: string;
    kecamatanStnkCode: string;
    kelurahanStnk: string;
    kelurahanStnkCode: string;
    vehicle: string;
    lastService: string;
    lastKM: number;
    status: string;
    brand: string;
    brandCode: string;
    model: string;
    modelCode: string;
    noIdBilling: string;
    customerNameBilling: string;
    nameDetail: string;
    noIdCurrentUser: string;
    addressCurrentUser: string;
    provinsiCurrentUser: string;
    provinsiCurrentUserCode: string;
    kabupatenCurrentUser: string;
    kabupatenCurrentUserCode: string;
    kecamatanCurrentUser: string;
    kecamatanCurrentUserCode: string;
    kelurahanCurrentUser: string;
    kelurahanCurrentUserCode: string;
    kodePosCurrentUser: string;
    phoneNumberCurrentUser: string;
}

interface ModalChassisForWoProps {
    onChassisSelect: (record: Partial<ChassisDataType>) => void;
}

type DataIndex = keyof ChassisDataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const ResizableTitle = (props: React.HTMLAttributes<any> & { onResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void; width: number; onDoubleClick: () => void; }) => {
    const { onResize, width, onDoubleClick, ...restProps } = props;

    if (!width) {
        return <th {...restProps} onDoubleClick={onDoubleClick} />;
    }

    return (
        <Resizable
            width={width}
            height={0}
            handle={
                <span
                    className="react-resizable-handle"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                />
            }
            onResize={onResize}
            draggableOpts={{ enableUserSelectHack: false }}
        >
            <th {...restProps} onDoubleClick={onDoubleClick} />
        </Resizable>
    );
};

const ModalChassisForWo: React.FC<ModalChassisForWoProps> = ({ onChassisSelect }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const { data, error } = useSWR<ChassisDataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/work-order/chassis`, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<ChassisDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>

            String(record[dataIndex])
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const [columns, setColumns] = useState<TableColumnsType<ChassisDataType>>([
        {
            title: 'Chassis No',
            dataIndex: 'chassisNo',
            key: 'chassisNo',
            width: 180,
            ...getColumnSearchProps('chassisNo'),
            sorter: (a, b) => (a.chassisNo || '').localeCompare(b.chassisNo || ''),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'Engine No',
            dataIndex: 'engineNo',
            key: 'engineNo',
            width: 180,
            ...getColumnSearchProps('engineNo'),
            sorter: (a, b) => (a.engineNo || '').localeCompare(b.engineNo || ''),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'No. Pol',
            dataIndex: 'noPol',
            key: 'noPol',
            width: 120,
            ...getColumnSearchProps('noPol'),
            sorter: (a, b) => (a.noPol || '').localeCompare(b.noPol || ''),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'STNK Name',
            dataIndex: 'stnkName',
            key: 'stnkName',
            width: 200,
            ...getColumnSearchProps('stnkName'),

            sorter: (a, b) => (a.stnkName || '').localeCompare(b.stnkName || ''),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'Vehicle',
            dataIndex: 'vehicle',
            key: 'vehicle',
            width: 200,
            ...getColumnSearchProps('vehicle'),
            sorter: (a, b) => (a.vehicle || '').localeCompare(b.vehicle || ''),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'Last Service',
            dataIndex: 'lastService',
            key: 'lastService',
            width: 150,
            ...getColumnSearchProps('lastService'),
            sorter: (a, b) => (a.lastService || '').localeCompare(b.lastService || ''),
            sortDirections: ['descend', 'ascend'],
            render: (text: string) => {
                if (!text || !dayjs(text).isValid()) {
                    return text;
                }
                return dayjs(text).format('DD/MM/YYYY');
            },
            onFilter: (value, record) => {
                const originalDate = record.lastService;
                if (!originalDate || !dayjs(originalDate).isValid()) {

                    return String(originalDate).toLowerCase().includes((value as string).toLowerCase());
                }
                const formattedDate = dayjs(originalDate).format('DD/MM/YYYY');
                return formattedDate.toLowerCase().includes((value as string).toLowerCase());
            },
            ellipsis: true,
        },
        {
            title: 'Last KM',
            dataIndex: 'lastKM',
            key: 'lastKM',
            width: 120,
            ...getColumnSearchProps('lastKM'),
            sorter: (a, b) => (a.lastKM || 0) - (b.lastKM || 0),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
            sortDirections: ['descend', 'ascend'],
            render: (text: string) => {
                if (text === '1') {
                    return 'Active';
                }
                if (text === '2') {
                    return 'Deactive';
                }
                return text;
            },
            filters: [
                { text: 'Active', value: '1' },
                { text: 'Deactive', value: '2' },
            ],
            onFilter: (value, record) => record.status === value,
            ellipsis: true,
        },
    ]);

    const autoSizeColumn = (dataIndex: DataIndex) => {
        if (!data) return;

        const FONT_STYLE = "14px 'Poppins', sans-serif";
        const PADDING = 32;

        const getTextWidth = (text: string, font: string): number => {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (context) {
                context.font = font;
                return context.measureText(text).width;
            }
            return 0;
        };

        const getRenderedText = (record: ChassisDataType, dataIndex: DataIndex): string => {
            const value = record[dataIndex];
            if (!value) return '';

            if (dataIndex === 'lastService') {
                if (!dayjs(String(value)).isValid()) {
                    return String(value);
                }
                return dayjs(String(value)).format('DD/MM/YYYY');
            }
            if (dataIndex === 'status') {
                if (value === '1') return 'Active';
                if (value === '2') return 'Deactive';
                return String(value);
            }
            return String(value);
        };

        const headerColumn = columns.find(col => col.key === dataIndex);
        const headerText = headerColumn && headerColumn.title ? String(headerColumn.title) : '';
        const headerWidth = getTextWidth(headerText, `600 ${FONT_STYLE}`) + PADDING;

        const cellWidths = data.map(item => {
            const cellText = getRenderedText(item, dataIndex);
            return getTextWidth(cellText, FONT_STYLE) + PADDING;
        });

        const maxContentWidth = Math.ceil(Math.max(headerWidth, ...cellWidths, 50));

        setColumns(prevColumns => {
            const index = prevColumns.findIndex(col => col.key === dataIndex);
            if (index === -1) return prevColumns;
            let newWidth = maxContentWidth;
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: newWidth,
            };
            return newColumns;
        });
    };

    const handleResize = (index: number) => (
        e: React.SyntheticEvent,
        { size }: ResizeCallbackData
    ) => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: size.width,
            };
            return newColumns;
        });
    };

    const resizableColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index),
            onDoubleClick: () => autoSizeColumn(column.dataIndex as DataIndex),
        }),
    }));

    const components = {
        header: {
            cell: ResizableTitle,
        },
    };

    const tableWidth = columns.reduce((sum, col) => sum + (col.width as number || 0), 0);

    return (
        <div id="isyy" style={transformStyle}>
            <div className="gjs-grid-row" id="ixefk">
                <div className="gjs-grid-column" id="itmxh">
                    <Table<ChassisDataType>
                        bordered
                        components={components}
                        columns={resizableColumns as any}
                        dataSource={data}
                        loading={!data && !error}
                        scroll={{ x: tableWidth }}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                onChassisSelect(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalChassisForWo;